import React, { useState, useEffect, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  InputGroup,
  InputGroupText,
  Badge,
} from "reactstrap"
import { Form } from "react-bootstrap"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons"
import { Link } from "react-router-dom"
import { UncontrolledTooltip } from "reactstrap"
import toastr from "toastr"
import TableContainer from "../../components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import apiURl from "url"

const Gardeners = () => {
  // Meta title
  document.title = "Gardeners"

  const navigate = useNavigate()

  // State management
  const [showPassword, setShowPassword] = useState(false)
  const [gardeners, setGardeners] = useState([])
  const [accountModal, setAccountModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteID, setDeleteID] = useState(null)
  const [editGardener, setEditGardener] = useState(null)
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    phone: "",
    password: "",
    experience: "",
    serviceAreas: "",
    availableHours: "",
    rate: "",
    profileImage: null,
    licenseProof: null,
  })

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  // Handle form input changes
  const handleInputChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleFileChange = e => {
    const { name } = e.target
    const file = e.target.files[0]
    if (file) {
      setFormData(prev => ({ ...prev, [name]: file }))
    }
  }

  // Fetch all gardeners
  const fetchAllGardeners = async () => {
    try {
      const response = await fetch(`${apiURl}/api/gardeners/fetchGardeners`)
      const json = await response.json()
      if (response.status === 200) setGardeners(json.gardeners.reverse())
    } catch (err) {
      console.error("Fetch Error:", err)
      toastr.error("Failed to fetch gardeners")
    }
  }

  // Fetch gardener data by ID for editing
  const fetchGardenerDataByID = async id => {
    try {
      const response = await fetch(
        `${apiURl}/api/gardeners/fetchGardenerByID`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id }),
        }
      )
      const json = await response.json()
      if (response.status === 200 && json.status === "success") {
        const gardenerData = json.GardenerDataByID[0]
        if (gardenerData) {
          setFormData({
            fullname: gardenerData.full_name || "",
            email: gardenerData.email_ || "",
            phone: gardenerData.phone_ || "",
            password: "", // Password not retrieved for security
            experience: gardenerData.experience || "",
            serviceAreas: gardenerData.service_areas || "",
            availableHours: gardenerData.available_hours || "",
            rate: gardenerData.rate || "",
            profileImage: gardenerData.profile_image || null,
            licenseProof: gardenerData.license_proof || null,
          })
          setEditGardener(gardenerData)
          setAccountModal(true)
        }
      }
    } catch (err) {
      console.error("Fetch Gardener Error:", err)
      toastr.error("Failed to fetch gardener data")
    }
  }

  // Handle save (create or update)
  const handleSaveGardener = async e => {
    e.preventDefault()

    // Form validation
    if (!formData.fullname.trim()) return toastr.error("Full Name is required")
    if (!formData.email.trim()) return toastr.error("Email is required")
    if (!formData.phone.trim()) return toastr.error("Phone Number is required")
    if (!formData.password.trim() && !editGardener)
      return toastr.error("Password is required")
    if (!formData.experience) return toastr.error("Experience is required")
    if (!formData.serviceAreas.trim())
      return toastr.error("Service Areas is required")
    if (!formData.availableHours.trim())
      return toastr.error("Available Hours is required")
    if (!formData.rate) return toastr.error("Rate is required")

    const method = editGardener ? "PUT" : "POST"
    const url = editGardener
      ? `${apiURl}/api/gardeners/edit-gardener/${editGardener.id}`
      : `${apiURl}/api/gardeners/add-gardener`

    const formPayload = new FormData()
    formPayload.append("fullname", formData.fullname)
    formPayload.append("email", formData.email)
    formPayload.append("phone", formData.phone)
    formPayload.append("password", formData.password || "")
    formPayload.append("experience", formData.experience)
    formPayload.append("serviceAreas", formData.serviceAreas)
    formPayload.append("availableHours", formData.availableHours)
    formPayload.append("rate", formData.rate)
    if (formData.profileImage) {
      formPayload.append("profileImage", formData.profileImage)
    }
    if (formData.licenseProof) {
      formPayload.append("licenseProof", formData.licenseProof)
    }

    try {
      const response = await fetch(url, {
        method,
        body: formPayload,
      })

      const json = await response.json()

      if (response.ok) {
        toastr.success(editGardener ? "Gardener updated!" : "Gardener added!")
        fetchAllGardeners()
        setAccountModal(false)
        setEditGardener(null)
        setFormData({
          fullname: "",
          email: "",
          phone: "",
          password: "",
          experience: "",
          serviceAreas: "",
          availableHours: "",
          rate: "",
          profileImage: null,
          licenseProof: null,
        })
      } else {
        toastr.error(json.message || "Failed to save gardener")
      }
    } catch (err) {
      console.error("Save Error:", err)
      toastr.error("Failed to save gardener")
    }
  }

  // Handle delete
  const deleteGardener = async () => {
    try {
      const response = await fetch(
        `${apiURl}/api/gardeners/delete-gardener/${deleteID}`,
        {
          method: "DELETE",
        }
      )
      if (response.ok) {
        toastr.success("Gardener deleted successfully")
        setDeleteModal(false)
        fetchAllGardeners()
      } else {
        toastr.error("Failed to delete gardener")
      }
    } catch (err) {
      console.error("Delete Error:", err)
      toastr.error("Failed to delete gardener")
    }
  }

  // Initialize component
  useEffect(() => {
    fetchAllGardeners()
  }, [])

  // Table columns
  const columns = useMemo(
    () => [
      {
        Header: "Full Name",
        accessor: "full_name",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Email",
        accessor: "email_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Phone",
        accessor: "phone_",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Experience (Years)",
        accessor: "experience",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Service Areas",
        accessor: "service_areas",
        Cell: cellProps => <span>{cellProps.value}</span>,
      },
      {
        Header: "Rate",
        accessor: "rate",
        Cell: cellProps => <span>${cellProps.value}/hr</span>,
      },
      {
        Header: "Status",
        accessor: "status_",
        Cell: ({ row }) => {
          const isActive = row.original.status_ === "Active"
          const handleToggle = async () => {
            const newStatus = isActive ? "Not Active" : "Active"
            try {
              const res = await fetch(
                `${apiURl}/api/gardeners/update-status/${row.original.id}`,
                {
                  method: "PUT",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify({ status_: newStatus }),
                }
              )
              if (res.ok) {
                toastr.success("Status updated successfully")
                fetchAllGardeners()
              } else {
                toastr.error("Failed to update status")
              }
            } catch (err) {
              toastr.error("Server error")
              console.error(err)
            }
          }
          return (
            <Form>
              <Form.Check
                type="switch"
                id={`status-switch-${row.original.id}`}
                label={
                  isActive ? (
                    <Badge className="bg-success">Active</Badge>
                  ) : (
                    <Badge className="bg-danger">Inactive</Badge>
                  )
                }
                defaultChecked={isActive}
                onChange={handleToggle}
                style={{
                  color: isActive ? "green" : "red",
                  fontWeight: "bold",
                }}
              />
            </Form>
          )
        },
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: cellProps => (
          <div className="d-flex gap-3">
            <Link
              to="#"
              onClick={() => fetchGardenerDataByID(cellProps.value)}
              className="text-primary"
            >
              <i
                className="mdi mdi-eye font-size-18"
                id={`viewtooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`viewtooltip-${cellProps.value}`}
              >
                View
              </UncontrolledTooltip>
            </Link>
            <Link
              to="#"
              className="text-danger"
              onClick={() => {
                setDeleteID(cellProps.value)
                setDeleteModal(true)
              }}
            >
              <i
                className="mdi mdi-delete font-size-18"
                id={`deletetooltip-${cellProps.value}`}
              />
              <UncontrolledTooltip
                placement="top"
                target={`deletetooltip-${cellProps.value}`}
              >
                Delete
              </UncontrolledTooltip>
            </Link>
          </div>
        ),
      },
    ],
    []
  )

  return (
    <React.Fragment>
      <DeleteModal
        show={deleteModal}
        onDeleteClick={deleteGardener}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xl="6">
              <h5 className="mt-2">Gardeners</h5>
            </Col>
            <Col>
              <div className="float-end">
                <Button
                  color="primary"
                  className="btn-sm w-md"
                  onClick={() => {
                    setFormData({
                      fullname: "",
                      email: "",
                      phone: "",
                      password: "",
                      experience: "",
                      serviceAreas: "",
                      availableHours: "",
                      rate: "",
                      profileImage: null,
                      licenseProof: null,
                    })
                    setEditGardener(null)
                    setAccountModal(true)
                  }}
                >
                  <i className="fa fa-fw fa-plus me-1" />
                  Create Gardener
                </Button>
              </div>
            </Col>
          </Row>

          <Row className="pt-3">
            <Col lg="12">
              <Card>
                <CardBody>
                  <TableContainer
                    columns={columns}
                    data={gardeners}
                    isGlobalFilter={true}
                    isAddOptions={false}
                    className="custom-table"
                    customPageSize={10}
                    FilterColumns={false}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Gardener Modal */}
      <Modal
        isOpen={accountModal}
        toggle={() => setAccountModal(!accountModal)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSaveGardener}>
          <ModalHeader toggle={() => setAccountModal(!accountModal)}>
            {editGardener ? "Edit Gardener" : "Create New Gardener"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Full Name *</Label>
                  <Input
                    type="text"
                    name="fullname"
                    value={formData.fullname}
                    onChange={handleInputChange}
                    placeholder="Enter full name"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Phone Number *</Label>
                  <Input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Password *</Label>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Enter password"
                      required={!editGardener}
                    />
                    <InputGroupText
                      onClick={togglePasswordVisibility}
                      style={{ cursor: "pointer" }}
                    >
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                      />
                    </InputGroupText>
                  </InputGroup>
                </FormGroup>

                <FormGroup>
                  <Label>Experience (Years) *</Label>
                  <Input
                    type="number"
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    placeholder="Enter years of experience"
                    min="0"
                    required
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>Service Areas *</Label>
                  <Input
                    type="text"
                    name="serviceAreas"
                    value={formData.serviceAreas}
                    onChange={handleInputChange}
                    placeholder="Enter service areas (e.g., City, Region)"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Available Hours *</Label>
                  <Input
                    type="text"
                    name="availableHours"
                    value={formData.availableHours}
                    onChange={handleInputChange}
                    placeholder="Enter available hours (e.g., 9AM-5PM)"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Rate per Hour/Job *</Label>
                  <Input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    placeholder="Enter rate (e.g., 50)"
                    min="0"
                    step="0.01"
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Profile Image (Optional)</Label>
                  <Input
                    type="file"
                    name="profileImage"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </FormGroup>
                {formData.profileImage && (
                  <div className="mb-3">
                    {formData.profileImage && (
                      <img
                        src={
                          typeof formData.profileImage === "string"
                            ? `${apiURl}${formData.profileImage}`
                            : URL.createObjectURL(formData.profileImage)
                        }
                        alt="Profile Preview"
                        style={{
                          width: "70px",
                          height: "70px",
                          objectFit: "cover",
                          borderRadius: "5px",
                          marginRight: "10px",
                        }}
                      />
                    )}
                  </div>
                )}

                <FormGroup>
                  <Label>License/ID Proof (Optional)</Label>
                  <Input
                    type="file"
                    name="licenseProof"
                    accept="image/*,application/pdf"
                    onChange={handleFileChange}
                  />
                </FormGroup>

                {formData.licenseProof && (
                  <div className="mb-3">
                    {formData.licenseProof && (
                      <img
                        src={
                          typeof formData.licenseProof === "string"
                            ? `${apiURl}${formData.licenseProof}`
                            : URL.createObjectURL(formData.licenseProof)
                        }
                        alt="Profile Preview"
                        style={{
                          width: "70px",
                          height: "70px",
                          objectFit: "cover",
                          borderRadius: "5px",
                          marginRight: "10px",
                        }}
                      />
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => {
                setFormData({
                  fullname: "",
                  email: "",
                  phone: "",
                  password: "",
                  experience: "",
                  serviceAreas: "",
                  availableHours: "",
                  rate: "",
                  profileImage: null,
                  licenseProof: null,
                })
                setEditGardener(null)
                setAccountModal(false)
              }}
            >
              Cancel
            </Button>
            <Button color="primary" type="submit">
              {editGardener ? "Update Gardener" : "Create Gardener"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  )
}

export default Gardeners
